//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import {
  getBubbleStats,
  getEmailSignupCount,
  getEmailSignupEnabled,
  getGraphStats,
  getLabels,
  getTouchpointStats,
} from '@apis/metrics/metricsApi';
import {
  EcommerceSessionType,
  IStatsRequest,
  InsightsSourceType,
} from '@apis/metrics/metricsApiModel';
import { initialBaseStatsRequest } from '@insights/insightsConstants';
import {
  EMPTY_UNIFIED_METRICS_BUBBLE_STATS_RESPONSE,
  IInsightsActivityStats,
  IInsightsBubbleStats,
  IInsightsExperienceStats,
  IInsightsGraphStats,
} from '@insights/redux/insightsPageModel';
import {
  fetchApiActivityStats,
  getTestVariationFromParams,
} from '@insights/redux/insightsPageUtils';
import { IInsightsLike2BuySummaryPageState } from '@insights/redux/like2buy/insightsLike2BuySummaryModel';
import { getMetadata } from '@insights/redux/metadata/insightsMetadataSlice';
import {
  getInsightsDateRange,
  getInsightsMetadataFlags,
} from '@insights/redux/selectors/getInsightsMetadata';
import { getLike2BuySummaryInsights } from '@insights/redux/selectors/getLike2BuySummaryInsights';
import { ILabel } from '@models/ILabel';
import Interval from '@models/Interval';
import { SliceNames } from '@redux/SliceNames';
import { createThunk } from '@redux/createThunk';
import { isDefined } from '@utils/filterPredicates';
import { getRequestErrorMessage } from '@utils/getRequestErrorMessage';
import IntervalHelper from '@utils/intervalHelper';

const initialState: IInsightsLike2BuySummaryPageState = {
  statsRequestBase: undefined,
  bubbleStats: undefined,
  experienceStats: undefined,
  graphStats: undefined,
  activityStats: initialBaseStatsRequest,
  allLabels: undefined,
  flags: {
    isFetchingBubbleStats: false,
    isFetchingExperienceStats: false,
    isFetchingGraphStats: false,
    isFetchingActivityStats: false,
    isFetchingLabels: false,
  },
  errors: {
    fetchBubbleStatsError: undefined,
    fetchExperienceStatsError: undefined,
    fetchGraphStatsError: undefined,
    fetchActivityStatsError: undefined,
  },
};

const { actions, reducer } = createSlice({
  name: SliceNames.InsightsLike2BuySummary,
  initialState,
  reducers: {
    requestBubbleStats(state) {
      state.flags.isFetchingBubbleStats = true;
      state.errors.fetchBubbleStatsError = undefined;
    },
    recieveBubbleStatsSucceeded(
      state,
      action: PayloadAction<IInsightsBubbleStats>,
    ) {
      state.flags.isFetchingBubbleStats = false;
      state.bubbleStats = action.payload;
    },
    recieveBubbleStatsFailed(state, action: PayloadAction<string>) {
      state.flags.isFetchingBubbleStats = false;
      state.errors.fetchBubbleStatsError = action.payload;
    },
    requestGraphStats(state) {
      state.flags.isFetchingGraphStats = true;
      state.errors.fetchGraphStatsError = undefined;
    },
    recieveGraphStatsSucceeded(
      state,
      action: PayloadAction<IInsightsGraphStats>,
    ) {
      state.flags.isFetchingGraphStats = false;
      state.graphStats = action.payload;
    },
    recieveGraphStatsFailed(state, action: PayloadAction<string>) {
      state.flags.isFetchingGraphStats = false;
      state.errors.fetchGraphStatsError = action.payload;
    },
    requestActivityStats(state) {
      state.flags.isFetchingActivityStats = true;
      state.errors.fetchActivityStatsError = undefined;
    },
    recieveActivityStatsSucceeded(
      state,
      action: PayloadAction<IInsightsActivityStats>,
    ) {
      state.flags.isFetchingActivityStats = false;
      state.activityStats = action.payload;
    },
    recieveActivityStatsFailed(state, action: PayloadAction<string>) {
      state.flags.isFetchingActivityStats = false;
      state.errors.fetchActivityStatsError = action.payload;
    },
    updateActivityStats(
      state,
      action: PayloadAction<Partial<IInsightsActivityStats>>,
    ) {
      state.activityStats = {
        ...state.activityStats,
        ...action.payload,
      };
    },
    requestExperienceStats(state) {
      state.flags.isFetchingExperienceStats = true;
      state.errors.fetchExperienceStatsError = undefined;
    },
    recieveExperienceStatsSucceeded(
      state,
      action: PayloadAction<IInsightsExperienceStats>,
    ) {
      state.flags.isFetchingExperienceStats = false;
      state.experienceStats = action.payload;
    },
    recieveExperienceStatsFailed(state, action: PayloadAction<string>) {
      state.flags.isFetchingExperienceStats = false;
      state.errors.fetchExperienceStatsError = action.payload;
    },
    requestLabels(state) {
      state.flags.isFetchingLabels = true;
      state.errors.fetchLabelsError = undefined;
    },
    recieveLabelsSucceeded(state, action: PayloadAction<ILabel[]>) {
      state.flags.isFetchingLabels = false;
      state.allLabels = action.payload;
    },
    recieveLabelsFailed(state, action: PayloadAction<string>) {
      state.flags.isFetchingLabels = false;
      state.errors.fetchLabelsError = action.payload;
    },
    setStatsRequestBase(state, action: PayloadAction<IStatsRequest>) {
      state.statsRequestBase = action.payload;
    },
    resetState(state) {
      return initialState;
    },
  },
});

export const fetchBubbleStats = createThunk(
  () => async (dispatch, getState) => {
    const { statsRequestBase } = getLike2BuySummaryInsights(getState());
    const dateRange = getInsightsDateRange(getState());

    if (statsRequestBase) {
      const statsRequest = isDefined(statsRequestBase.comparisonInterval)
        ? statsRequestBase
        : {
            ...statsRequestBase,
            comparisonInterval: Interval.fromDateRange(
              IntervalHelper.getComparisonDateRange(dateRange),
            ),
          };

      dispatch(actions.requestBubbleStats());

      try {
        const bubbleStatsRes = await getBubbleStats(statsRequest);
        const emailStatsRes = await getEmailSignupCount({
          ...statsRequestBase,
          sourceType: [InsightsSourceType.L2B],
        });
        const isEmailSignupsEnabled = await getEmailSignupEnabled({
          ...statsRequestBase,
          sourceType: [InsightsSourceType.L2B],
        });
        const bubbleStats: IInsightsBubbleStats = {
          stats:
            bubbleStatsRes[0] ?? EMPTY_UNIFIED_METRICS_BUBBLE_STATS_RESPONSE,
          emailSignups: emailStatsRes,
          isEmailSignupsEnabled: isEmailSignupsEnabled,
        };
        dispatch(actions.recieveBubbleStatsSucceeded(bubbleStats));
      } catch (e) {
        dispatch(
          actions.recieveBubbleStatsFailed(
            getRequestErrorMessage(e) ??
              '[fetchBubbleStats] Failed to fetch bubble stats',
          ),
        );
      }
    }
  },
);

export const fetchGraphStats = createThunk(
  (storeId: string | undefined) => async (dispatch, getState) => {
    const { statsRequestBase } = getLike2BuySummaryInsights(getState());

    if (statsRequestBase) {
      dispatch(actions.requestGraphStats());

      try {
        // Once we have multiple like2buy_v2 networks we should store all of the stores in insights metadata to use for both the sidebar and these requests
        const [like2BuyGraphStatsRes, like2BuyV2GraphStatsRes] =
          await Promise.all([
            getGraphStats({
              ...statsRequestBase,
              sourceType: [InsightsSourceType.L2B],
            }),
            getGraphStats({
              ...statsRequestBase,
              sourceType: [InsightsSourceType.L2B_V2],
              sourceId: storeId,
            }),
          ]);

        const graphStats: IInsightsGraphStats = {
          stats: [
            ...like2BuyGraphStatsRes.map((data) => ({
              ...data,
              label: 'Instagram',
            })),
            ...like2BuyV2GraphStatsRes.map((data) => ({
              ...data,
              label: 'TikTok',
            })),
          ],
        };
        dispatch(actions.recieveGraphStatsSucceeded(graphStats));
      } catch (e) {
        dispatch(
          actions.recieveGraphStatsFailed(
            getRequestErrorMessage(e) ??
              '[fetchGraphStats] Failed to fetch graph stats',
          ),
        );
      }
    }
  },
);

export const fetchActivityStats = createThunk(
  () => async (dispatch, getState) => {
    const { activityStats, statsRequestBase } =
      getLike2BuySummaryInsights(getState());
    const { contentType, limit, offset, sortDir, sortKey } = activityStats;

    if (statsRequestBase) {
      dispatch(actions.requestActivityStats());

      try {
        const itemProductStatsRequest = {
          ...statsRequestBase,
          sortKey,
          sortDirection: sortDir,
          limit,
          offset,
        };
        const activityStatsRes: IInsightsActivityStats =
          await fetchApiActivityStats(
            contentType,
            itemProductStatsRequest,
            activityStats,
          );
        dispatch(actions.recieveActivityStatsSucceeded(activityStatsRes));
      } catch (e) {
        dispatch(
          actions.recieveActivityStatsFailed(
            getRequestErrorMessage(e) ??
              '[fetchActivityStats] Failed to fetch activity stats',
          ),
        );
      }
    }
  },
);

export const fetchExperienceStats = createThunk(
  () => async (dispatch, getState) => {
    const { statsRequestBase } = getLike2BuySummaryInsights(getState());

    if (statsRequestBase) {
      dispatch(actions.requestExperienceStats());
      try {
        const touchpointStatsRes = await getTouchpointStats(statsRequestBase);
        dispatch(
          actions.recieveExperienceStatsSucceeded({
            stats: touchpointStatsRes,
          }),
        );
      } catch (e) {
        dispatch(
          actions.recieveExperienceStatsFailed(
            getRequestErrorMessage(e) ??
              '[fetchExperienceStats] Failed to fetch experience stats',
          ),
        );
      }
    }
  },
);

export const fetchLabels = createThunk(() => async (dispatch, getState) => {
  const { statsRequestBase } = getLike2BuySummaryInsights(getState());

  if (statsRequestBase) {
    dispatch(actions.requestLabels());

    try {
      const labelsResponse = await getLabels(statsRequestBase);
      dispatch(
        actions.recieveLabelsSucceeded(
          labelsResponse.map((label) => {
            return {
              label: label.label,
              count: label.count,
              value: label.label,
            };
          }),
        ),
      );
    } catch (e) {
      dispatch(
        actions.recieveLabelsFailed(
          getRequestErrorMessage(e) ?? '[fetchLabels] Failed to fetch labels',
        ),
      );
    }
  }
});

export const initStatsRequestBase = createThunk(
  (ecommerceSessionType: EcommerceSessionType, storeId: string | undefined) =>
    async (dispatch, getState) => {
      const { hasFetchedMetadata } = getInsightsMetadataFlags(getState());

      if (!hasFetchedMetadata) {
        await dispatch(getMetadata());
      }
      const { createdOn, dateRange } = getState().insights.metadata;
      void dispatch(
        getStats(
          {
            interval: IntervalHelper.fromDateRange(dateRange, createdOn),
            ecommerceSessionType: ecommerceSessionType,
            sourceType: [InsightsSourceType.L2B_V2, InsightsSourceType.L2B],
            testVariation: getTestVariationFromParams(),
          },
          storeId,
        ),
      );
    },
);

export const getStats = createThunk(
  (statsRequestBase: IStatsRequest, storeId: string | undefined) =>
    (dispatch) => {
      dispatch(actions.setStatsRequestBase(statsRequestBase));
      void dispatch(fetchBubbleStats());
      void dispatch(fetchGraphStats(storeId));
      void dispatch(fetchActivityStats());
      void dispatch(fetchLabels());
      void dispatch(fetchExperienceStats());
    },
);

export const { resetState, updateActivityStats } = actions;

export { reducer };
